import { useMemo } from 'react'
import { ArrowUpRight, ShieldCheck } from 'lucide-react'
import { Link } from 'react-router-dom'

import { AppShell } from '@/components/px/AppShell'
import { DensityNote } from '@/components/px/DensityNote'
import { KpiCard } from '@/components/px/KpiCard'
import { PhaseRibbon } from '@/components/px/PhaseRibbon'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill, type StatusTone } from '@/components/px/StatusPill'
import { projectNav } from '@/components/px/nav'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  ADOPTION,
  GO_LIVE_PAGE,
  GO_NO_GO_CRITERIA,
  GO_NO_GO_PANEL,
  HYPERCARE,
  LINKS,
  OWNERSHIP,
  RUNBOOK,
  RUNBOOK_STEPS,
  densityNote,
  goLiveKpis,
  stepAnchorId,
  type OwnerRef,
  type RunbookStatusKind,
} from '@/demo/content/goLive'
import { heroProject, personById } from '@/demo/data'
import { cn } from '@/lib/utils'

/**
 * 3.8 Go-live — the cutover runbook, eleven weeks before the cutover.
 *
 * The surface is deliberately NOT a decision card. The runbook is a draft: the
 * cutover planner maintains it, four go/no-go criteria are open, and nothing is
 * signed — so the ownership panel states who will sign and when, and shows no
 * approved-by chip. Rendering an approval for an unsigned document would be the
 * one dishonest pixel on the page.
 *
 * Every row and criterion lands somewhere real: the dependency links to the step
 * it waits on (which is on this page), and each criterion links to the surface
 * that proves it.
 */

const STATUS_TONE: Record<RunbookStatusKind, StatusTone> = {
  ready: 'done',
  rehearsed: 'done',
  depends: 'review',
  staffed: 'neutral',
}

function Avatar({ initial }: { initial: string }) {
  return (
    <span
      aria-hidden
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-people-bg text-xs font-semibold text-people-ink"
    >
      {initial}
    </span>
  )
}

function PersonLink({ personId, label }: { personId: string; label?: string }) {
  const person = personById(personId)
  return (
    <Link
      to={LINKS.person(person.id)}
      className="font-medium text-accent-foreground hover:underline"
    >
      {label ?? person.name}
    </Link>
  )
}

/**
 * Owner treatments differ by KIND, not by styling whim: a PX person carries an
 * avatar and a profile link; a client-side owner carries neither (they are not
 * a resource in the pool); an agent-prepared step names the agent and the
 * person who runs it, each linked to its own record.
 */
function OwnerCell({ owner }: { owner: OwnerRef }) {
  if (owner.kind === 'client') {
    return (
      <span className="flex flex-col leading-tight">
        <span className="text-[13px] font-medium text-ink-soft">{owner.label}</span>
        <span className="text-xs text-muted-foreground">{owner.note}</span>
      </span>
    )
  }

  if (owner.kind === 'agent-and-person') {
    const person = personById(owner.personId)
    return (
      <span className="flex items-center gap-2">
        <Avatar initial={person.initial} />
        <span className="flex flex-col leading-tight">
          <span className="text-[13px]">
            <Link to={LINKS.planner} className="font-medium text-accent-foreground hover:underline">
              {owner.agentLabel}
            </Link>
            <span className="text-muted-foreground"> + </span>
            <PersonLink personId={owner.personId} label={owner.personLabel} />
          </span>
          <span className="text-xs text-muted-foreground">{person.role}</span>
        </span>
      </span>
    )
  }

  const person = personById(owner.personId)
  return (
    <span className="flex items-center gap-2">
      <Avatar initial={person.initial} />
      <span className="flex flex-col leading-tight">
        <span className="text-[13px]">
          <PersonLink personId={person.id} />
        </span>
        <span className="text-xs text-muted-foreground">
          {person.role} · {person.office}
        </span>
      </span>
    </span>
  )
}

export default function GoLivePage() {
  const project = heroProject()
  const nav = useMemo(() => projectNav(project), [project])
  const kpis = useMemo(() => goLiveKpis(GO_NO_GO_CRITERIA.length), [])
  const density = useMemo(() => densityNote(RUNBOOK_STEPS.length), [])

  return (
    <AppShell
      moduleLabel={GO_LIVE_PAGE.moduleLabel}
      breadcrumb={project.name}
      contextPill={GO_LIVE_PAGE.contextPill}
      nav={nav}
    >
      {/* PX-W6.6 (AC-1.6): this surface renders demo content and had no
          badge. Never hide a surface — badge it. */}
      <div className="mb-3">
        <ConceptBadge />
      </div>
      <div className="w-full">
        <header className="max-w-4xl">
          <h1 className="font-serif text-[26px] font-semibold leading-8 text-foreground">
            {GO_LIVE_PAGE.title}
          </h1>
          <p className="mt-1.5 text-sm text-ink-soft">{GO_LIVE_PAGE.standfirst}</p>
        </header>

        <PhaseRibbon current={GO_LIVE_PAGE.phase} className="mt-4" />

        <p className="mt-2 text-xs text-muted-foreground">
          {GO_LIVE_PAGE.phaseNote}{' '}
          <Link to={LINKS.overview} className="font-medium text-accent-foreground hover:underline">
            {GO_LIVE_PAGE.overviewLinkLabel}
          </Link>
          {' · '}
          <Link to={LINKS.test} className="font-medium text-accent-foreground hover:underline">
            {GO_LIVE_PAGE.testLinkLabel}
          </Link>
        </p>

        <section className="mt-5 grid grid-cols-2 gap-3 xl:grid-cols-4" aria-label={GO_LIVE_PAGE.title}>
          {kpis.map((kpi) => (
            <KpiCard
              key={kpi.key}
              label={kpi.label}
              figure={kpi.figure}
              qualifier={kpi.qualifier}
              className={cn(kpi.weight === 'review' && 'border-review/50 bg-review-bg')}
            />
          ))}
        </section>

        {/* ── The runbook ─────────────────────────────────────────────── */}
        <section
          aria-labelledby="runbook-heading"
          className="mt-7 rounded-lg border border-border bg-card"
        >
          <div className="border-b border-border px-4 py-3">
            <SectionLabel>{RUNBOOK.label}</SectionLabel>
            <h2 id="runbook-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
              {RUNBOOK.heading}
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">{RUNBOOK.caption}</p>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12 px-4">{RUNBOOK.columns.number}</TableHead>
                <TableHead>{RUNBOOK.columns.step}</TableHead>
                <TableHead>{RUNBOOK.columns.owner}</TableHead>
                <TableHead>{RUNBOOK.columns.window}</TableHead>
                <TableHead>{RUNBOOK.columns.rollback}</TableHead>
                <TableHead className="px-4">{RUNBOOK.columns.status}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {RUNBOOK_STEPS.map((step) => (
                <TableRow
                  key={step.key}
                  id={stepAnchorId(step.number)}
                  className={cn('scroll-mt-20', step.statusKind === 'depends' && 'bg-review-bg')}
                >
                  <TableCell className="px-4 font-mono text-[13px] tabular-nums text-muted-foreground">
                    {step.number}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'max-w-[26rem] whitespace-normal text-[13px] text-foreground',
                      step.statusKind === 'depends' && 'font-medium'
                    )}
                  >
                    {step.step}
                  </TableCell>
                  <TableCell>
                    <OwnerCell owner={step.owner} />
                  </TableCell>
                  <TableCell className="v-figure text-[13px] tabular-nums text-ink-soft">
                    {step.window}
                  </TableCell>
                  <TableCell className="text-[13px]">
                    {step.rollback === 'Restore point' ? (
                      <span className="inline-flex items-center gap-1.5 text-ink-soft">
                        <ShieldCheck className="h-3.5 w-3.5 text-done" aria-hidden />
                        {step.rollback}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">{step.rollback}</span>
                    )}
                  </TableCell>
                  <TableCell className="px-4">
                    {step.dependsOnNumber !== undefined ? (
                      <a
                        href={`#${stepAnchorId(step.dependsOnNumber)}`}
                        className="inline-flex rounded-full hover:underline"
                      >
                        <StatusPill tone={STATUS_TONE[step.statusKind]}>{step.statusLabel}</StatusPill>
                        <span className="sr-only"> — {RUNBOOK.dependsLinkSuffix}</span>
                      </a>
                    ) : (
                      <StatusPill tone={STATUS_TONE[step.statusKind]}>{step.statusLabel}</StatusPill>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>

        {/* ── Go / no-go ──────────────────────────────────────────────── */}
        <section
          aria-labelledby="go-no-go-heading"
          className="mt-6 rounded-lg border border-border bg-card px-4 py-4"
        >
          <SectionLabel>{GO_NO_GO_PANEL.label}</SectionLabel>
          <h2 id="go-no-go-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
            {GO_NO_GO_PANEL.heading}
          </h2>
          <p className="mt-0.5 max-w-3xl text-xs text-muted-foreground">{GO_NO_GO_PANEL.caption}</p>

          <ul className="mt-3">
            {GO_NO_GO_CRITERIA.map((criterion) => (
              <li
                key={criterion.key}
                className="v-row flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2.5"
              >
                <span className="flex min-w-0 flex-col gap-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <StatusPill tone="review">{criterion.statusLabel}</StatusPill>
                    <span className="text-sm font-medium text-foreground">{criterion.criterion}</span>
                  </span>
                  <span className="text-xs text-muted-foreground">{criterion.standing}</span>
                </span>
                <Link
                  to={criterion.href}
                  className="inline-flex shrink-0 items-center gap-1 text-[13px] font-medium text-accent-foreground hover:underline"
                >
                  {criterion.linkLabel}
                  <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
        </section>

        {/* ── Hypercare + adoption ────────────────────────────────────── */}
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <section
            aria-labelledby="hypercare-heading"
            className="rounded-lg border border-border bg-card px-4 py-4"
          >
            <SectionLabel>{HYPERCARE.label}</SectionLabel>
            <h2 id="hypercare-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
              {HYPERCARE.heading}
            </h2>
            <ul className="mt-2">
              {HYPERCARE.lines.map((line) => (
                <li key={line} className="v-row py-2 text-sm leading-relaxed text-ink-soft">
                  {line}
                </li>
              ))}
            </ul>
            <Link
              to={HYPERCARE.requirementsHref}
              className="mt-2.5 inline-flex items-center gap-1 text-[13px] font-medium text-accent-foreground hover:underline"
            >
              {HYPERCARE.requirementsLinkLabel}
              <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          </section>

          <section
            aria-labelledby="adoption-heading"
            className="rounded-lg border border-border bg-accent px-4 py-4"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <SectionLabel>{ADOPTION.label}</SectionLabel>
              <StatusPill tone="proposed">{ADOPTION.agentChip}</StatusPill>
            </div>
            <h2 id="adoption-heading" className="sr-only">
              {ADOPTION.label}
            </h2>
            {ADOPTION.lines.map((line) => (
              <p key={line} className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground">
                {line}
              </p>
            ))}

            <h3 className="mt-4 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              {ADOPTION.consultantsLabel}
            </h3>
            <ul className="mt-1.5 flex flex-wrap gap-x-6 gap-y-2">
              {ADOPTION.consultants.map((consultant) => {
                const person = personById(consultant.personId)
                return (
                  <li key={consultant.personId} className="flex items-center gap-2">
                    <Avatar initial={person.initial} />
                    <span className="flex flex-col leading-tight">
                      <span className="text-[13px]">
                        <PersonLink personId={person.id} />
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {consultant.note} · {person.office}
                      </span>
                    </span>
                  </li>
                )
              })}
            </ul>
          </section>
        </div>

        {/* ── Ownership: a draft, stated as a draft ───────────────────── */}
        <section
          aria-labelledby="ownership-heading"
          className="mt-6 rounded-lg border border-border bg-card px-4 py-4"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <SectionLabel>{OWNERSHIP.label}</SectionLabel>
            <span className="flex flex-wrap items-center gap-1.5">
              <StatusPill tone="proposed">{OWNERSHIP.agentChipLabel}</StatusPill>
              <StatusPill tone="review">{OWNERSHIP.draftStatusLabel}</StatusPill>
            </span>
          </div>
          <h2 id="ownership-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
            {OWNERSHIP.heading}
          </h2>

          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-ink-soft">{OWNERSHIP.draftedLine}</p>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground">{OWNERSHIP.ownerLine}</p>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-ink-soft">{OWNERSHIP.goNoGoLine}</p>

          <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border pt-2.5 text-[13px]">
            <Link to={LINKS.planner} className="font-medium text-accent-foreground hover:underline">
              {OWNERSHIP.plannerLinkLabel}
              <ArrowUpRight className="ml-1 inline h-3.5 w-3.5" aria-hidden />
            </Link>
            <PersonLink personId={OWNERSHIP.ownerPersonId} label={OWNERSHIP.ownerLinkLabel} />
          </p>

          <p className="mt-2.5 max-w-3xl text-xs italic text-muted-foreground">{OWNERSHIP.reason}</p>
        </section>

        <DensityNote>{density}</DensityNote>
      </div>
    </AppShell>
  )
}
